import { Button } from '@radix-ui/themes';
import {
  ArrowDown, ArrowLeft, ArrowRight, ArrowUp,
  ChevronsDown, ChevronsUp, CornerDownLeft, Maximize2,
} from 'lucide-react';
import type { KeyName } from '../lib/xtermInput';

type Key = { key: KeyName; label: string; icon?: typeof ArrowUp; title?: string; wide?: boolean };

/** Phone-accessible terminal keys. Cursor encoding follows xterm's live mode. */
const CODEX_KEYS: Key[] = [
  { key: 'esc', label: 'Esc', title: 'Прервать / назад' },
  { key: 'ctrl-c', label: '^C', title: 'Ctrl+C — оборвать' },
  { key: 'pgup', label: '', icon: ChevronsUp, title: 'Страница вверх' },
  { key: 'pgdn', label: '', icon: ChevronsDown, title: 'Страница вниз' },
  { key: 'ctrl-t', label: '', icon: Maximize2, title: 'Ctrl+T — история Codex' },
  { key: 'up', label: '', icon: ArrowUp, title: 'Вверх' },
  { key: 'down', label: '', icon: ArrowDown, title: 'Вниз' },
  { key: 'left', label: '', icon: ArrowLeft },
  { key: 'right', label: '', icon: ArrowRight },
  { key: 'tab', label: 'Tab' },
  { key: 'enter', label: '', icon: CornerDownLeft, title: 'Enter — выбрать', wide: true },
];

/** Shell controls retain their own shortcuts and command history. */
const SHELL_KEYS: Key[] = [
  { key: 'ctrl-c', label: '^C', title: 'Ctrl+C — оборвать команду' },
  { key: 'ctrl-d', label: '^D', title: 'Ctrl+D — конец ввода / выйти из шелла' },
  { key: 'ctrl-z', label: '^Z', title: 'Ctrl+Z — приостановить (fg вернёт)' },
  { key: 'ctrl-l', label: '^L', title: 'Ctrl+L — очистить экран' },
  { key: 'ctrl-r', label: '^R', title: 'Ctrl+R — поиск по истории' },
  { key: 'ctrl-u', label: '^U', title: 'Ctrl+U — стереть строку' },
  { key: 'up', label: '', icon: ArrowUp, title: 'Вверх — предыдущая команда' },
  { key: 'down', label: '', icon: ArrowDown, title: 'Вниз — следующая команда' },
  { key: 'left', label: '', icon: ArrowLeft },
  { key: 'right', label: '', icon: ArrowRight },
  { key: 'tab', label: 'Tab', title: 'Tab — автодополнение' },
  { key: 'esc', label: 'Esc' },
  { key: 'enter', label: '', icon: CornerDownLeft, title: 'Enter', wide: true },
];

export function KeyBar({
  onKey,
  disabled,
  variant = 'codex',
}: {
  onKey: (key: KeyName) => void;
  disabled: boolean;
  variant?: 'codex' | 'shell';
}) {
  return (
    <div className="keybar">
      {(variant === 'shell' ? SHELL_KEYS : CODEX_KEYS).map((k) => {
        const Icon = k.icon;
        return (
          <Button
            key={k.key}
            size="2"
            variant={k.key === 'enter' ? 'solid' : 'soft'}
            color={k.key === 'ctrl-c' ? 'red' : k.key === 'enter' ? 'jade' : 'gray'}
            disabled={disabled}
            title={k.title}
            onClick={() => onKey(k.key)}
            style={k.wide ? { minWidth: 56 } : undefined}
          >
            {Icon ? <Icon size={15} /> : k.label}
          </Button>
        );
      })}
    </div>
  );
}
